import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { DateField } from '../../../../components/inputs/DateField';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { orgUnitsApi } from '../../../human-resources/organization/organizationApi';
import type { OrgUnitOption } from '../../../human-resources/organization/organizationTypes';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { surveyQuestionnairesApi } from '../surveyApi';
import {
  SURVEY_QUESTION_TYPE,
  SURVEY_STATUS,
  isChoiceType,
  type MySurveyResponse,
  type SubmitSurveyResponseRequest,
  type SurveyQuestionnaire,
  type SurveyQuestionnaireDetail,
  type SurveyQuestionnaireQuestion,
  type SurveyResponseState,
  type SurveyStatusValue,
} from '../surveyTypes';

const ID = 'survey-answer';

/**
 * WHERE the form reads and writes, and on which keys. Two surfaces answer one questionnaire: the
 * administrator's (`surveys.*`, the default) and the employee's own under Self Service
 * (`my-surveys.*`, route-scoped to the caller). One form, so the two cannot drift apart.
 * Pass a MODULE-LEVEL constant — it is an effect dependency.
 */
export interface SurveyAnswerSource {
  // Only the questions are read — the employee surface sends no administrator counts.
  get: (surveyId: string, questionnaireId: string, signal?: AbortSignal) => Promise<Pick<SurveyQuestionnaireDetail, 'questions'>>;
  myResponse: (surveyId: string, questionnaireId: string, signal?: AbortSignal) => Promise<SurveyResponseState>;
  respond: (surveyId: string, questionnaireId: string, request: SubmitSurveyResponseRequest) => Promise<MySurveyResponse>;
  /** The chart, for a Division question and its sector. */
  units: (signal?: AbortSignal) => Promise<OrgUnitOption[]>;
  /** The key that answers. */
  respondPermission: string;
  /** The key that answers for another employee, or null where the surface never does. */
  onBehalfPermission: string | null;
}

const ADMINISTRATION: SurveyAnswerSource = {
  get: (surveyId, id, signal) => surveyQuestionnairesApi.get(surveyId, id, signal),
  myResponse: (surveyId, id, signal) => surveyQuestionnairesApi.myResponse(surveyId, id, signal),
  respond: (surveyId, id, request) => surveyQuestionnairesApi.respond(surveyId, id, request),
  units: (signal) => orgUnitsApi.options(undefined, signal),
  respondPermission: 'surveys.respond',
  onBehalfPermission: 'surveys.respond-on-behalf',
};

interface Draft {
  optionIds: string[];
  text: string;
  number: string;
  date: string;
  time: string;
  orgUnitId: string;
  sectorOrgUnitId: string;
}

const EMPTY: Draft = { optionIds: [], text: '', number: '', date: '', time: '', orgUnitId: '', sectorOrgUnitId: '' };

/** What answering a question depends on beyond its own draft. */
interface AnswerContext {
  /** A Name question is answered — by the respondent's own record, or by an employee picked. */
  nameAnswered: boolean;
  /** The live units beneath a unit, at any depth — a Division's possible sectors. */
  sectorsOf: (unitId: string) => OrgUnitOption[];
}

/** Every live unit anywhere beneath `unitId`, from the flat options feed and its parent links. */
function unitsBeneath(units: readonly OrgUnitOption[], unitId: string): OrgUnitOption[] {
  const found: OrgUnitOption[] = [];
  const queue = [unitId];

  while (queue.length > 0) {
    const parent = queue.shift();

    for (const unit of units) {
      if (unit.parentId === parent && !found.includes(unit)) {
        found.push(unit);
        queue.push(unit.id);
      }
    }
  }

  return found;
}

/** Whether a question has an answer, read by its type. */
function isAnswered(question: SurveyQuestionnaireQuestion, draft: Draft, ctx: AnswerContext): boolean {
  switch (question.type) {
    case SURVEY_QUESTION_TYPE.Name:
      return ctx.nameAnswered;
    case SURVEY_QUESTION_TYPE.Division:
      return draft.orgUnitId !== '';
    case SURVEY_QUESTION_TYPE.MultipleChoice:
    case SURVEY_QUESTION_TYPE.Checkbox:
    case SURVEY_QUESTION_TYPE.Dropdown:
      return draft.optionIds.length > 0;
    case SURVEY_QUESTION_TYPE.ShortParagraph:
    case SURVEY_QUESTION_TYPE.LongParagraph:
      return draft.text.trim().length > 0;
    case SURVEY_QUESTION_TYPE.Scale:
      return draft.number !== '';
    case SURVEY_QUESTION_TYPE.Date:
      return draft.date !== '';
    case SURVEY_QUESTION_TYPE.Time:
      return draft.time !== '';
    default:
      return false;
  }
}

/**
 * Whether a REQUIRED question is satisfied — answered, and for a Division with units beneath it,
 * given a sector too. The same rule the server applies.
 */
function requirementMet(question: SurveyQuestionnaireQuestion, draft: Draft, ctx: AnswerContext): boolean {
  if (!question.isRequired) return true;
  if (!isAnswered(question, draft, ctx)) return false;

  return (
    question.type !== SURVEY_QUESTION_TYPE.Division ||
    ctx.sectorsOf(draft.orgUnitId).length === 0 ||
    draft.sectorOrgUnitId !== ''
  );
}

/** A plausible answer for each question, so the form fills in one click. */
function sampleAnswers(questions: SurveyQuestionnaireQuestion[], units: readonly OrgUnitOption[]): Record<string, Draft> {
  const pick = <T,>(items: T[]) => items[Math.floor(Math.random() * items.length)];
  const answers: Record<string, Draft> = {};

  for (const q of questions) {
    const draft: Draft = { ...EMPTY };

    if (q.type === SURVEY_QUESTION_TYPE.Checkbox) {
      draft.optionIds = q.options.filter(() => Math.random() < 0.5).map((o) => o.id);
      if (draft.optionIds.length === 0 && q.options.length > 0) draft.optionIds = [q.options[0].id];
    } else if (isChoiceType(q.type) && q.options.length > 0) {
      draft.optionIds = [pick(q.options).id];
    } else if (q.type === SURVEY_QUESTION_TYPE.ShortParagraph) {
      draft.text = 'Sample answer';
    } else if (q.type === SURVEY_QUESTION_TYPE.LongParagraph) {
      draft.text = 'A sample answer, written so the form can be tried end to end.';
    } else if (q.type === SURVEY_QUESTION_TYPE.Scale) {
      draft.number = String(q.scaleMin + Math.floor(Math.random() * (q.scaleMax - q.scaleMin + 1)));
    } else if (q.type === SURVEY_QUESTION_TYPE.Date) {
      draft.date = new Date().toLocaleDateString('en-CA');
    } else if (q.type === SURVEY_QUESTION_TYPE.Time) {
      draft.time = '09:00';
    } else if (q.type === SURVEY_QUESTION_TYPE.Division && units.length > 0) {
      const unit = pick([...units]);
      const sectors = unitsBeneath(units, unit.id);

      draft.orgUnitId = unit.id;
      draft.sectorOrgUnitId = sectors.length > 0 ? pick(sectors).id : '';
    }

    answers[q.id] = draft;
  }

  return answers;
}

/**
 * A questionnaire as its respondent answers it — what clicking a questionnaire's title opens.
 *
 * **Anybody who can read the survey sees the form**; answering it takes the source's respond key AND an
 * account linked to a live employee record, and each is a refusal the form ANNOUNCES rather than
 * one Submit discovers. An employee who has already answered sees their own answers, read only.
 * The respondent is always the signed-in account — never chosen here.
 */
export function SurveyAnswerModal({
  open,
  surveyId,
  surveyActive,
  surveyStatus,
  questionnaire,
  source = ADMINISTRATION,
  onClose,
  onAnswered,
}: {
  open: boolean;
  surveyId: string;
  surveyActive: boolean;
  /**
   * Where the survey is in its run. A survey is answered only while Started — the SERVER's rule;
   * passing it lets the form say so before anybody fills it in. Omitted (the employee's surface,
   * which lists only open surveys), the server's refusal still stands.
   */
  surveyStatus?: SurveyStatusValue;
  questionnaire: Pick<SurveyQuestionnaire, 'id' | 'title' | 'description' | 'isActive'>;
  source?: SurveyAnswerSource;
  onClose: () => void;
  onAnswered: () => void;
}) {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canPickEmployee =
    source.onBehalfPermission != null && hasPermission(source.onBehalfPermission) && hasPermission('personnel.read');

  const [questions, setQuestions] = useState<SurveyQuestionnaireQuestion[] | null>(null);
  const [state, setState] = useState<SurveyResponseState | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  const [answers, setAnswers] = useState<Record<string, Draft>>({});
  const [units, setUnits] = useState<OrgUnitOption[]>([]);
  const [employees, setEmployees] = useState<{ value: string; label: string }[]>([]);
  // Answering on behalf: the employee picked on the Name question.
  const [respondentPersonnelId, setRespondentPersonnelId] = useState('');

  /*
    Division questions the reader has chosen for themselves. A Division DEFAULTS to the named
    employee's current unit — and stops the moment the reader picks one, so a later change of
    employee never overwrites a choice they made.
  */
  const [touchedDivisions, setTouchedDivisions] = useState<Set<string>>(() => new Set());
  const [remarks, setRemarks] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      source.get(surveyId, questionnaire.id, controller.signal),
      source.myResponse(surveyId, questionnaire.id, controller.signal),
    ])
      .then(([detail, mine]) => {
        if (controller.signal.aborted) return;

        setQuestions(detail.questions);
        setState(mine);

        const hasName = detail.questions.some((q) => q.type === SURVEY_QUESTION_TYPE.Name);
        // Picking: on the Name question, or — for a key holder who is no employee — on the form's own.
        const pickingFor = canPickEmployee && (hasName || !mine.isEmployee);

        // The chart, for Division questions — on the source's respond key. A reader
        // without it gets an empty list, which the read-only form never needs.
        if (detail.questions.some((q) => q.type === SURVEY_QUESTION_TYPE.Division)) {
          source
            .units(controller.signal)
            .then((next) => {
              if (controller.signal.aborted) return;

              setUnits(next);

              // Answering as themselves, unanswered: every Division starts on their own current unit.
              const mineUnit = mine.myOrgUnitId;

              if (!pickingFor && mine.response == null && mineUnit != null && next.some((u) => u.id === mineUnit)) {
                setAnswers((current) => {
                  const filled = { ...current };

                  for (const q of detail.questions) {
                    if (q.type === SURVEY_QUESTION_TYPE.Division) {
                      filled[q.id] = { ...(filled[q.id] ?? EMPTY), orgUnitId: mineUnit, sectorOrgUnitId: '' };
                    }
                  }

                  return filled;
                });
              }
            })
            .catch(() => undefined);
        }

        /*
          The employee list, for answering on behalf: one page of personnel and ONE batch resolve
          for their names, which are never stored here. A name that does not resolve falls back to
          the employee number, which identifies the row without naming the person.
        */
        if (pickingFor) {
          personnelApi
            .list({ pageSize: 100 }, controller.signal)
            .then(async (page) => {
              const ids = page.items.map((r) => r.personId).filter((id): id is string => id != null);
              const names = ids.length > 0 ? await personsApi.resolve(ids, controller.signal).catch(() => []) : [];
              const byPerson = new Map(names.map((n) => [n.personId, n.displayName]));

              if (controller.signal.aborted) return;

              setEmployees(
                page.items.map((r) => ({
                  value: r.id,
                  label: (r.personId != null ? byPerson.get(r.personId) : undefined) ?? r.employeeNumber,
                })),
              );
            })
            .catch(() => undefined);
        }

        // Already answered: the form shows what was given, as it was given. Not when answering for
        // somebody else — that is a fresh response each time.
        if (mine.response != null && !pickingFor) {
          const given: Record<string, Draft> = {};

          for (const a of mine.response.answers) {
            given[a.questionId] = {
              optionIds: a.optionIds,
              text: a.text ?? '',
              number: a.number != null ? String(a.number) : '',
              date: a.date ?? '',
              time: a.time ? a.time.slice(0, 5) : '',
              orgUnitId: a.orgUnitId ?? '',
              sectorOrgUnitId: a.sectorOrgUnitId ?? '',
            };
          }

          setAnswers(given);
          setRemarks(mine.response.remarks ?? '');
        }
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setLoadError(caught instanceof ApiError ? caught : new ApiError('Could not load this questionnaire.', 0));
      });

    return () => controller.abort();
  }, [surveyId, questionnaire.id, canPickEmployee, source]);

  const hasNameQuestion = (questions ?? []).some((q) => q.type === SURVEY_QUESTION_TYPE.Name);

  /*
    ANSWERING ON BEHALF — a super user picks the employee. It needs the key and the employee list
    (`personnel.read`); the server checks the key again before it runs. The pick is ON the Name
    question where there is one; otherwise, for a key holder who is not an employee (the admin —
    product call, 09 Oct 2026: it answers EVERY survey), on an Employee picker heading the form.
    A key holder who IS an employee answers a questionnaire without a Name question as themselves.
  */
  const onBehalf = canPickEmployee && (hasNameQuestion || state?.isEmployee === false);
  const pickerOnForm = onBehalf && !hasNameQuestion;
  const answered = state?.response != null && !onBehalf;

  /*
    Why the reader cannot answer, if they cannot — the first that applies, in the order a reader
    would fix them. Null means the form is live.
  */
  const blocked = !state
    ? null
    : answered
      ? `You answered this on ${formatDateTime(state.response!.respondedAt)}. Your answers are shown below.`
      : !surveyActive || !questionnaire.isActive
        ? 'This questionnaire is deleted, so it can no longer be answered.'
        : surveyStatus === SURVEY_STATUS.NotStarted
          ? 'This survey has not started yet, so it cannot be answered. Start it from the banner on the survey, or from the settings of its questionnaire.'
          : surveyStatus === SURVEY_STATUS.Done
            ? 'This survey has ended, so it can no longer be answered.'
            : !hasPermission(source.respondPermission)
          ? 'You can view this questionnaire, but your account is not permitted to answer it.'
          : !state.isEmployee && !onBehalf
            ? 'Only an employee can answer a survey. Your account is not linked to a personnel record.'
            : null;

  const canAnswer = state !== null && blocked === null;
  const readOnly = !canAnswer || saving;

  /** Picks the employee answered for, and defaults every untouched Division to their current unit. */
  async function pickRespondent(personnelId: string) {
    setRespondentPersonnelId(personnelId);

    if (!personnelId || !questions) return;

    // A failed lookup leaves the Divisions as they are: the default is a convenience, never a rule.
    const unitId = await surveyQuestionnairesApi.employeeUnit(personnelId).catch(() => null);

    if (unitId == null || !units.some((u) => u.id === unitId)) return;

    setAnswers((current) => {
      const filled = { ...current };

      for (const q of questions) {
        if (q.type === SURVEY_QUESTION_TYPE.Division && !touchedDivisions.has(q.id)) {
          filled[q.id] = { ...(filled[q.id] ?? EMPTY), orgUnitId: unitId, sectorOrgUnitId: '' };
        }
      }

      return filled;
    });
  }

  const draftOf = (id: string) => answers[id] ?? EMPTY;
  const setDraft = (id: string, patch: Partial<Draft>) =>
    setAnswers((current) => ({ ...current, [id]: { ...(current[id] ?? EMPTY), ...patch } }));

  const ctx: AnswerContext = {
    nameAnswered: onBehalf ? respondentPersonnelId !== '' : state?.isEmployee === true,
    sectorsOf: (unitId) => (unitId ? unitsBeneath(units, unitId) : []),
  };

  const complete = (questions ?? []).every((q) => requirementMet(q, draftOf(q.id), ctx));
  const canSave =
    canAnswer && complete && (!onBehalf || respondentPersonnelId !== '') && confirmed && !saving;

  async function submit() {
    if (!canSave || !questions) return;

    setSaving(true);
    setError(null);

    try {
      await source.respond(surveyId, questionnaire.id, {
        respondentPersonnelId: onBehalf ? respondentPersonnelId : null,
        remarks: remarks.trim() || null,
        // A Name answer is never sent: the server records the respondent's own employee link.
        answers: questions
          .filter((q) => q.type !== SURVEY_QUESTION_TYPE.Name && isAnswered(q, draftOf(q.id), ctx))
          .map((q) => {
            const d = draftOf(q.id);

            return {
              questionId: q.id,
              optionIds: d.optionIds,
              text: d.text.trim() || null,
              number: d.number === '' ? null : Number(d.number),
              date: d.date || null,
              time: d.time || null,
              orgUnitId: d.orgUnitId || null,
              sectorOrgUnitId: d.sectorOrgUnitId || null,
            };
          }),
      });

      toast.success(
        onBehalf ? `Submitted the answers to "${questionnaire.title}".` : `Submitted your answers to "${questionnaire.title}".`,
      );
      onAnswered();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Your answers could not be submitted.', 0);

      setError(failure);

      // A radio question's id sits on each OPTION, so its refusal points at the first one.
      const fieldIds: Record<string, string> = {};

      for (const q of questions) {
        const radio = q.type === SURVEY_QUESTION_TYPE.MultipleChoice || q.type === SURVEY_QUESTION_TYPE.Scale;
        const first =
          q.type === SURVEY_QUESTION_TYPE.Scale ? String(q.scaleMin) : (q.options[0]?.id ?? '');
        const target = radio ? `${ID}-${q.id}-${first}` : `${ID}-${q.id}`;

        fieldIds[`Answers.${q.id}`] = target;
        fieldIds[`answers.${q.id}`] = target;
        fieldIds[`Answers.${q.id}.Sector`] = `${ID}-${q.id}-sector`;
        fieldIds[`answers.${q.id}.sector`] = `${ID}-${q.id}-sector`;

        if (q.type === SURVEY_QUESTION_TYPE.Name) {
          fieldIds.RespondentPersonnelId = `${ID}-${q.id}`;
          fieldIds.respondentPersonnelId = `${ID}-${q.id}`;
        }
      }

      if (pickerOnForm) {
        fieldIds.RespondentPersonnelId = `${ID}-respondent`;
        fieldIds.respondentPersonnelId = `${ID}-respondent`;
      }

      focusValidationError(failure, { idPrefix: ID, fieldIds });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={questionnaire.title}
      size="2xl"
      id={`${ID}-modal`}
      // Only a form that can be submitted writes anything, so only that one is generated or ticked.
      autogenerate={
        canAnswer && questions
          ? {
              onGenerate: () => {
                setAnswers(sampleAnswers(questions, units));
                if (onBehalf && employees.length > 0) {
                  setRespondentPersonnelId(employees[Math.floor(Math.random() * employees.length)].value);
                }
              },
              disabled: saving,
            }
          : undefined
      }
      confirm={canAnswer ? { id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined}
      footer={
        canAnswer ? (
          <>
            <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              id={`${ID}-submit`}
              variant="primary"
              onClick={() => void submit()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </>
        ) : (
          <Button id={`${ID}-close`} variant="secondary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {loadError ? (
        <ErrorState message={loadError.message} correlationId={loadError.correlationId} />
      ) : !questions || !state ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {questionnaire.description ? (
            <p className="whitespace-pre-line text-sm text-slate-600">{questionnaire.description}</p>
          ) : null}

          {blocked ? (
            <p role="status" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              {blocked}
            </p>
          ) : null}

          {error ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs text-red-700">{error.message}</p>
            </div>
          ) : null}

          {/* No Name question to pick on: the employee answered for heads the form, required. */}
          {pickerOnForm && canAnswer ? (
            <div className="max-w-sm">
              <Select
                id={`${ID}-respondent`}
                label="Employee"
                required
                description="The employee this response is recorded for. Your account is not an employee, so you answer on their behalf."
                placeholder="Choose an employee"
                value={respondentPersonnelId}
                onChange={(next) => void pickRespondent(next)}
                options={employees}
                remote
                disabled={readOnly}
                error={error?.fieldError('respondentPersonnelId')}
              />
            </div>
          ) : null}

          {questions.length === 0 ? <p className="text-sm text-slate-500">This questionnaire has no questions yet.</p> : null}

          {questions.map((question, index) => (
            <div key={question.id} className="flex flex-col gap-3 rounded-xl border border-slate-200 p-4">
              <div className="flex gap-2">
                <span className="shrink-0 font-mono text-sm tabular-nums text-slate-500">{index + 1}.</span>
                <div className="flex min-w-0 flex-col gap-1">
                  <p className="text-sm text-slate-800">
                    {question.text}
                    {question.isRequired ? <span className="ml-1 text-red-600">*</span> : null}
                  </p>
                  {question.description ? (
                    <p className="whitespace-pre-line text-xs text-slate-500">{question.description}</p>
                  ) : null}
                </div>
              </div>

              <AnswerInput
                question={question}
                draft={draftOf(question.id)}
                disabled={readOnly}
                error={
                  error?.fieldError(`answers.${question.id}`) ??
                  (question.type === SURVEY_QUESTION_TYPE.Name ? error?.fieldError('respondentPersonnelId') : undefined)
                }
                sectorError={error?.fieldError(`answers.${question.id}.sector`)}
                onChange={(patch) => {
                  // Choosing a division by hand ends the default for this question.
                  if (question.type === SURVEY_QUESTION_TYPE.Division && 'orgUnitId' in patch) {
                    setTouchedDivisions((current) => new Set(current).add(question.id));
                  }

                  setDraft(question.id, patch);
                }}
                units={units}
                sectors={ctx.sectorsOf(draftOf(question.id).orgUnitId)}
                onBehalf={onBehalf}
                employees={employees}
                respondentPersonnelId={respondentPersonnelId}
                onRespondent={(next) => void pickRespondent(next)}
                myName={state.myName ?? null}
              />
            </div>
          ))}

          {questions.length > 0 ? (
            <Textarea
              id={`${ID}-remarks`}
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              error={error?.fieldError('remarks')}
              disabled={readOnly}
            />
          ) : null}
        </div>
      )}
    </Modal>
  );
}

/** The control a respondent answers one question with, by its type — every one a shared input. */
function AnswerInput({
  question,
  draft,
  disabled,
  error,
  sectorError,
  onChange,
  units,
  sectors,
  onBehalf,
  employees,
  respondentPersonnelId,
  onRespondent,
  myName,
}: {
  question: SurveyQuestionnaireQuestion;
  draft: Draft;
  disabled: boolean;
  error: string | undefined;
  sectorError: string | undefined;
  onChange: (patch: Partial<Draft>) => void;
  units: OrgUnitOption[];
  /** The live units beneath the chosen division, at any depth. Empty means no sector field. */
  sectors: OrgUnitOption[];
  onBehalf: boolean;
  employees: { value: string; label: string }[];
  respondentPersonnelId: string;
  onRespondent: (personnelId: string) => void;
  myName: string | null;
}) {
  const id = `${ID}-${question.id}`;
  const label = question.text;

  switch (question.type) {
    case SURVEY_QUESTION_TYPE.MultipleChoice:
      return (
        // No group label: the card's heading above already states the question.
        <RadioGroup
          id={id}
          value={draft.optionIds[0] ?? ''}
          onChange={(next) => onChange({ optionIds: [next] })}
          options={question.options.map((o) => ({ value: o.id, label: o.text }))}
          columns={1}
          disabled={disabled}
          error={error}
        />
      );

    case SURVEY_QUESTION_TYPE.Checkbox:
      return (
        <div id={id} role="group" aria-label={label} className="flex flex-col gap-2">
          {question.options.map((o) => (
            <label key={o.id} htmlFor={`${id}-${o.id}`} className="flex items-center gap-2.5">
              <Checkbox
                id={`${id}-${o.id}`}
                checked={draft.optionIds.includes(o.id)}
                onChange={(e) =>
                  onChange({
                    optionIds: e.target.checked
                      ? [...draft.optionIds, o.id]
                      : draft.optionIds.filter((x) => x !== o.id),
                  })
                }
                disabled={disabled}
              />
              <span className="text-sm text-slate-700">{o.text}</span>
            </label>
          ))}
          {error ? <p className="text-xs text-red-600" role="alert">{error}</p> : null}
        </div>
      );

    case SURVEY_QUESTION_TYPE.Dropdown:
      return (
        <div className="max-w-sm">
          <Select
            id={id}
            ariaLabel={label}
            placeholder="Choose"
            value={draft.optionIds[0] ?? ''}
            onChange={(next) => onChange({ optionIds: next ? [next] : [] })}
            options={question.options.map((o) => ({ value: o.id, label: o.text }))}
            sort="none"
            disabled={disabled}
            error={error}
          />
        </div>
      );

    case SURVEY_QUESTION_TYPE.ShortParagraph:
      return (
        <TextField
          id={id}
          label={label}
          labelHidden
          value={draft.text}
          onChange={(e) => onChange({ text: e.target.value })}
          maxLength={500}
          disabled={disabled}
          error={error}
        />
      );

    case SURVEY_QUESTION_TYPE.LongParagraph:
      return (
        <Textarea
          id={id}
          label={label}
          value={draft.text}
          onChange={(e) => onChange({ text: e.target.value })}
          disabled={disabled}
          error={error}
        />
      );

    case SURVEY_QUESTION_TYPE.Scale: {
      const steps = Array.from({ length: question.scaleMax - question.scaleMin + 1 }, (_, i) => question.scaleMin + i);

      // The end labels ride on their numbers, so the scale reads "1 — Poor … 5 — Excellent".
      return (
        <RadioGroup
          id={id}
          value={draft.number}
          onChange={(next) => onChange({ number: next })}
          options={steps.map((n) => ({
            value: String(n),
            label:
              n === question.scaleMin && question.scaleMinLabel
                ? `${n} — ${question.scaleMinLabel}`
                : n === question.scaleMax && question.scaleMaxLabel
                  ? `${n} — ${question.scaleMaxLabel}`
                  : String(n),
          }))}
          disabled={disabled}
          error={error}
        />
      );
    }

    case SURVEY_QUESTION_TYPE.Date:
      return (
        <div className="max-w-xs">
          {/* DateField requires a visible label; "Date" says what the box takes without repeating the question. */}
          <DateField id={id} label="Date" value={draft.date} onChange={(next) => onChange({ date: next })} disabled={disabled} error={error} />
        </div>
      );

    case SURVEY_QUESTION_TYPE.Time:
      return (
        <div className="max-w-xs">
          <TextField
            id={id}
            type="time"
            label={label}
            labelHidden
            className="font-mono"
            value={draft.time}
            onChange={(e) => onChange({ time: e.target.value })}
            disabled={disabled}
            error={error}
          />
        </div>
      );

    case SURVEY_QUESTION_TYPE.Name:
      // A super user picks the employee they answer for; everyone else IS the answer, read only.
      return onBehalf ? (
        <div className="max-w-sm">
          <Select
            id={id}
            ariaLabel={label}
            placeholder="Choose an employee"
            value={respondentPersonnelId}
            onChange={onRespondent}
            options={employees}
            remote
            disabled={disabled}
            error={error}
          />
        </div>
      ) : (
        <p id={id} className="text-sm text-slate-700">
          {myName ?? <span className="text-slate-400">Your name, from your employee record</span>}
        </p>
      );

    case SURVEY_QUESTION_TYPE.Division:
      return (
        <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
          <Select
            id={id}
            label="Division"
            placeholder="Choose a division"
            value={draft.orgUnitId}
            // A new division clears the sector: the old one is not beneath it.
            onChange={(next) => onChange({ orgUnitId: next, sectorOrgUnitId: '' })}
            options={units.map((u) => ({ value: u.id, label: u.name, sublabel: u.orgUnitTypeName }))}
            remote
            disabled={disabled}
            error={error}
          />

          {/* Triggered: only when the chosen division has units beneath it. */}
          {sectors.length > 0 ? (
            <Select
              id={`${id}-sector`}
              label="Sector"
              required={question.isRequired}
              placeholder="Choose a sector"
              value={draft.sectorOrgUnitId}
              onChange={(next) => onChange({ sectorOrgUnitId: next })}
              options={sectors.map((u) => ({ value: u.id, label: u.name, sublabel: u.orgUnitTypeName }))}
              remote
              disabled={disabled}
              error={sectorError}
            />
          ) : null}
        </div>
      );

    default:
      return null;
  }
}
